<template>
  <div class="simulation-shell">
    <div class="ambient ambient-one" aria-hidden="true" />
    <div class="ambient ambient-two" aria-hidden="true" />
    <div class="background-grid" aria-hidden="true" />

    <header class="registration-topbar">
      <button class="nav-back" type="button" aria-label="Zurück" @click="goBack">
        <v-icon size="22">mdi-arrow-left</v-icon>
      </button>
      <div class="brand-lockup">
        <span class="brand-mark">WCF</span>
        <span class="brand-copy">HYROX<br />SIMULATION</span>
      </div>
      <div v-if="event && step <= 3" class="top-progress desktop-top-progress" aria-label="Anmeldefortschritt">
        <div class="progress-track">
          <span :class="{ filled: step >= 2 }" />
          <span :class="{ filled: step >= 3 }" />
        </div>
        <div v-for="item in 3" :key="item" class="progress-node" :class="{ active: step === item, complete: step > item }">
          <v-icon v-if="step > item" size="14">mdi-check</v-icon>
          <span v-else>{{ item }}</span>
          <small>{{ ["Details", "Prüfen", "Zahlen"][item - 1] }}</small>
        </div>
      </div>
      <div v-if="event && step <= 3" class="mobile-top-progress" aria-label="Anmeldefortschritt">
        <div class="mobile-progress-track">
          <i :style="{ width: `${overallProgressPercent}%` }"></i>
        </div>
        <div
          v-for="item in 5"
          :key="item"
          class="mobile-progress-node"
          :class="{
            active: overallProgressStep === item,
            complete: overallProgressStep > item,
          }">
          <v-icon v-if="overallProgressStep > item" size="12">mdi-check</v-icon>
          <span v-else>{{ item }}</span>
        </div>
      </div>
      <div v-else class="secure-label">
        <v-icon size="16">mdi-shield-check-outline</v-icon>
        <span>Sichere Anmeldung</span>
      </div>
    </header>

    <main class="simulation-container">
      <section v-if="loading" class="loading-stage" aria-live="polite">
        <div class="loader-orbit">
          <v-progress-circular indeterminate color="#f4d35e" size="56" width="3" />
        </div>
        <strong>Event wird vorbereitet</strong>
        <span>Einen kurzen Moment…</span>
      </section>

      <section v-else-if="loadError" class="state-card error-state">
        <div class="state-icon error-icon">
          <v-icon size="34">mdi-cloud-alert-outline</v-icon>
        </div>
        <p class="eyebrow">Verbindung unterbrochen</p>
        <h1>Das Event konnte nicht geladen werden.</h1>
        <p>{{ loadError }}</p>
        <v-btn class="primary-button mt-5" large :loading="loading" @click="loadEvent"> Nochmals versuchen </v-btn>
      </section>

      <template v-else-if="event">
        <section class="event-stage">
          <div class="event-date-block" aria-label="Eventdatum">
            <strong>{{ eventDateParts.day }}</strong>
            <span>{{ eventDateParts.month }}</span>
            <small>{{ eventDateParts.year }}</small>
          </div>
          <div class="event-intro">
            <div class="event-kicker">
              <span class="live-dot" />
              ST. GALLEN · RACE DAY
            </div>
            <h1 class="event-title" aria-label="HYROX Simulation">
              <span>HYROX</span>
              <span>SIMULATION</span>
            </h1>
            <p>Probiere HYROX erstmals ohne Wettkampfdruck aus oder nutze die Simulation als Vorbereitung auf deinen nächsten Wettkampf.</p>
            <div class="event-facts">
              <span>
                <v-icon size="17">mdi-map-marker-outline</v-icon>
                {{ event.locationName }}
              </span>
              <span>
                <v-icon size="17">mdi-tshirt-crew-outline</v-icon>
                Unisex-T-Shirt inklusive
              </span>
              <span>
                <v-icon size="17">mdi-run-fast</v-icon>
                Run draussen · bei jedem Wetter
              </span>
              <span>
                <v-icon size="17">mdi-trophy-outline</v-icon>
                Ohne offizielles Ranking
              </span>
            </div>
          </div>
          <div class="event-badge" aria-hidden="true">
            <span>SIM</span>
            <strong>{{ eventDateParts.day }}</strong>
            <small>{{ eventDateParts.month }}</small>
          </div>
        </section>

        <section v-if="pendingRegistration && step === 1" class="state-card pending-state">
          <div class="state-icon pending-icon">
            <v-icon size="34">mdi-timer-sand</v-icon>
          </div>
          <p class="eyebrow">Platz reserviert</p>
          <h2>Deine Zahlung wurde unterbrochen.</h2>
          <p>
            Die bisherige Reservation für
            <strong>{{ pendingRegistration.categoryLabel }}</strong> wird freigegeben, bevor du die Anmeldung neu startest.
          </p>
          <v-alert v-if="paymentError" type="error" text dense class="form-alert mt-5">
            {{ paymentError }}
          </v-alert>
          <div class="pending-actions">
            <v-btn class="primary-button" large :loading="cancellingPayment" @click="cancelPendingPayment()">
              Anmeldung neu starten
              <v-icon right size="20">mdi-arrow-right</v-icon>
            </v-btn>
            <v-btn text class="secondary-button" :disabled="cancellingPayment" @click="cancelPendingPayment({ redirectHome: true })">
              Zur Startseite
            </v-btn>
          </div>
        </section>

        <section v-else-if="!event.registrationOpen && ![4, 5].includes(step)" class="state-card closed-state">
          <div class="state-icon closed-icon">
            <v-icon size="34">mdi-calendar-lock-outline</v-icon>
          </div>
          <p class="eyebrow">Online-Anmeldung</p>
          <h2>Die Anmeldung ist momentan geschlossen.</h2>
          <p>Das Anmeldefenster ist noch nicht geöffnet oder bereits abgelaufen. Bei Fragen erreichst du uns direkt.</p>
          <div class="contact-pills">
            <a :href="`mailto:${event.contactEmail}`">
              <v-icon size="17">mdi-email-outline</v-icon>
              {{ event.contactEmail }}
            </a>
            <a :href="`tel:${event.contactPhone}`">
              <v-icon size="17">mdi-phone-outline</v-icon>
              {{ event.contactPhone }}
            </a>
          </div>
        </section>

        <section v-else class="flow-layout">
          <aside class="event-rail">
            <div class="rail-card">
              <div class="rail-heading">
                <span>DEIN RACE DAY</span>
                <v-icon color="#f4d35e" size="19">mdi-lightning-bolt</v-icon>
              </div>

              <div class="rail-detail">
                <v-icon size="19">mdi-calendar-blank-outline</v-icon>
                <div>
                  <small>Datum</small>
                  <strong>{{ formatDate(event.eventDate) }}</strong>
                </div>
              </div>
              <div class="rail-detail">
                <v-icon size="19">mdi-map-marker-outline</v-icon>
                <div>
                  <small>Location</small>
                  <strong>{{ event.locationName }}</strong>
                  <span v-if="event.locationAddress">{{ event.locationAddress }}</span>
                </div>
              </div>
              <div class="rail-detail">
                <v-icon size="19">mdi-clock-fast</v-icon>
                <div>
                  <small>Anmeldeschluss</small>
                  <strong>{{ formatDate(event.registrationClosesAt) }}</strong>
                </div>
              </div>

              <div class="rail-divider" />
              <div class="rail-price">
                <small>Startgeld ab</small>
                <strong>CHF {{ Number(event.pricing.singleMember).toFixed(0) }}</strong>
                <span>Unisex-T-Shirt, Finisher-Patch, Fotos und Verpflegung inklusive</span>
              </div>

              <div class="rail-divider" />
              <div class="mini-schedule">
                <div class="mini-schedule-title">Startplan</div>
                <div
                  v-for="category in availableCategories"
                  :key="category.key"
                  class="mini-schedule-row"
                  :class="{ selected: category.key === categoryKey }">
                  <span>{{ category.label }}</span>
                  <strong>{{ category.startTime }}</strong>
                </div>
              </div>
            </div>
          </aside>

          <v-card ref="flowCard" dark flat class="flow-card">
            <v-window v-model="step" touchless>
              <v-window-item :value="1">
                <div class="flow-content">
                  <div class="flow-heading">
                    <p class="eyebrow">Details · {{ formStep }} von 3</p>
                    <h2>{{ formStepMeta.title }}</h2>
                    <p>{{ formStepMeta.description }}</p>
                  </div>

                  <div v-if="isTrainer" class="trainer-benefit" role="note">
                    <span class="trainer-benefit-icon">
                      <v-icon size="23">mdi-whistle</v-icon>
                    </span>
                    <div>
                      <div class="trainer-benefit-heading">
                        <strong>Dein Trainer-Rabatt</strong>
                        <span>Single 50 % · Double 25 %</span>
                      </div>
                      <p>
                        Der Rabatt wird automatisch auf den regulären Anmeldepreis angewendet. Hilfst du vor Ort mit, melde dich bitte nicht selbst an
                        – Melanie trägt dich kostenlos ein.
                      </p>
                    </div>
                  </div>

                  <div class="form-stepper" aria-label="Detailfortschritt">
                    <div class="form-step-track">
                      <i :style="{ width: `${formProgressPercent}%` }"></i>
                    </div>
                    <div
                      v-for="item in formSteps"
                      :key="item.number"
                      class="form-step-node"
                      :class="{
                        active: formStep === item.number,
                        complete: formStep > item.number,
                      }">
                      <span>
                        <v-icon v-if="formStep > item.number" size="13"> mdi-check </v-icon>
                        <template v-else>{{ item.number }}</template>
                      </span>
                      <small>{{ item.label }}</small>
                    </div>
                  </div>

                  <div v-if="formStep === 1" class="event-guidance" aria-label="Wichtige Informationen zur HYROX Simulation">
                    <div class="guidance-item">
                      <span class="guidance-icon">
                        <v-icon size="21">mdi-account-heart-outline</v-icon>
                      </span>
                      <div>
                        <strong>Erstes HYROX oder Vorbereitung</strong>
                        <p>Ideal, um HYROX erstmals ohne Wettkampfdruck auszuprobieren oder dich auf kommende Wettkämpfe vorzubereiten.</p>
                      </div>
                    </div>
                    <div class="guidance-item">
                      <span class="guidance-icon">
                        <v-icon size="21">mdi-run-fast</v-icon>
                      </span>
                      <div>
                        <strong>Laufstrecke draussen · bei jedem Wetter</strong>
                        <p>Die Laufrunden finden draussen rund um den Friedhof statt – auch bei Regen.</p>
                      </div>
                    </div>
                    <div class="guidance-item">
                      <span class="guidance-icon">
                        <v-icon size="21">mdi-tshirt-crew-outline</v-icon>
                      </span>
                      <div>
                        <strong>Unisex-T-Shirt und Finisher-Patch</strong>
                        <p>Das Unisex-T-Shirt erhältst du bei der Anmeldung vor dem Briefing. Nach dem Finish bekommst du den Finisher-Patch.</p>
                      </div>
                    </div>
                    <div class="guidance-item">
                      <span class="guidance-icon">
                        <v-icon size="21">mdi-clipboard-check-outline</v-icon>
                      </span>
                      <div>
                        <strong>Kein offizielles Ranking</strong>
                        <p>Mit maximal einem Judge pro Station achtest du selbst auf die Regeln und eine saubere Ausführung.</p>
                      </div>
                    </div>
                  </div>

                  <v-form ref="registrationForm" v-model="formValid">
                    <transition name="form-slide" mode="out-in">
                      <section v-if="formStep === 1" key="category-step" class="form-section">
                        <div class="section-heading">
                          <span class="section-number">01</span>
                          <div>
                            <h3>Kategorie</h3>
                            <p>Single oder Double – wähle deinen Startblock.</p>
                          </div>
                        </div>

                        <div class="category-grid">
                          <button
                            v-for="category in availableCategories"
                            :key="category.key"
                            type="button"
                            class="category-option"
                            :class="{
                              selected: categoryKey === category.key,
                              soldout: category.soldOut,
                            }"
                            :disabled="category.soldOut"
                            :aria-pressed="categoryKey === category.key"
                            @click="selectCategory(category)">
                            <span class="category-icon">
                              <v-icon size="22">{{ categoryIcon(category) }}</v-icon>
                            </span>
                            <span class="category-copy">
                              <strong>{{ category.label }}</strong>
                              <small>
                                Briefing {{ category.briefingTime }} · Start
                                {{ category.startTime }}
                              </small>
                            </span>
                            <span class="availability" :class="{ scarce: !category.soldOut && category.remainingRegistrations <= 5 }">
                              {{ categoryAvailability(category) }}
                            </span>
                            <v-icon v-if="categoryKey === category.key" class="selected-check" size="18"> mdi-check-circle </v-icon>
                          </button>
                        </div>

                        <div v-if="selectedCategory && selectedCategory.composition === 'mixed'" class="inline-note">
                          <v-icon size="18">mdi-information-outline</v-icon>
                          Double Mixed besteht aus einer Frau und einem Mann.
                        </div>
                        <div class="auto-advance-note">
                          <v-icon size="17">mdi-cursor-default-click-outline</v-icon>
                          Kategorie auswählen – danach geht es automatisch weiter.
                        </div>
                      </section>

                      <section v-else-if="formStep === 2" key="participants-step" class="form-section">
                        <button type="button" class="current-selection" @click="goToFormStep(1)">
                          <span class="category-icon">
                            <v-icon size="20">{{ categoryIcon(selectedCategory) }}</v-icon>
                          </span>
                          <span>
                            <small>GEWÄHLTE KATEGORIE</small>
                            <strong>{{ selectedCategory && selectedCategory.label }}</strong>
                          </span>
                          <v-icon size="18">mdi-pencil-outline</v-icon>
                        </button>

                        <div class="section-heading">
                          <span class="section-number">02</span>
                          <div>
                            <h3>{{ isDouble ? "Euer Team" : "Deine Angaben" }}</h3>
                            <p>Deine App-Daten sind bereits sicher übernommen.</p>
                          </div>
                        </div>

                        <div class="participant-panel captain-panel">
                          <div class="participant-label">
                            <span class="avatar-token">
                              {{ userInitials }}
                            </span>
                            <div>
                              <small>{{ isDouble ? "TEAM-CAPTAIN" : "TEILNEHMER/IN" }}</small>
                              <strong> {{ user && user.vorname }} {{ user && user.nachname }} </strong>
                            </div>
                            <v-icon class="verified-icon" size="20">mdi-check-decagram</v-icon>
                          </div>
                          <div class="identity-grid">
                            <div>
                              <small>E-Mail</small>
                              <span>{{ user && user.email }}</span>
                            </div>
                            <div class="shirt-field">
                              <v-select
                                v-model="mainShirtSize"
                                :items="event.shirtSizes"
                                label="Unisex-T-Shirt-Grösse*"
                                outlined
                                dense
                                hide-details="auto"
                                :rules="[requiredRule]"
                                @change="resetQuote" />
                            </div>
                          </div>
                        </div>

                        <v-expand-transition>
                          <div v-if="isDouble" class="participant-panel partner-panel">
                            <div class="participant-label">
                              <span class="avatar-token partner-token">02</span>
                              <div>
                                <small>DOUBLE-PARTNER/IN</small>
                                <strong>Partnerdaten ergänzen</strong>
                              </div>
                            </div>
                            <v-row dense class="field-grid">
                              <v-col cols="12" sm="6">
                                <v-text-field
                                  v-model.trim="partner.firstName"
                                  label="Vorname*"
                                  outlined
                                  dense
                                  hide-details="auto"
                                  :rules="[requiredRule]"
                                  @input="resetQuote" />
                              </v-col>
                              <v-col cols="12" sm="6">
                                <v-text-field
                                  v-model.trim="partner.lastName"
                                  label="Nachname*"
                                  outlined
                                  dense
                                  hide-details="auto"
                                  :rules="[requiredRule]"
                                  @input="resetQuote" />
                              </v-col>
                              <v-col cols="12" sm="7">
                                <v-text-field
                                  v-model.trim="partner.email"
                                  label="E-Mail*"
                                  type="email"
                                  outlined
                                  dense
                                  hide-details="auto"
                                  :rules="[requiredRule, emailRule]"
                                  @input="resetQuote" />
                              </v-col>
                              <v-col cols="12" sm="5">
                                <v-text-field
                                  v-model="partner.birthdate"
                                  label="Geburtsdatum*"
                                  type="date"
                                  outlined
                                  dense
                                  hide-details="auto"
                                  :rules="[requiredRule]"
                                  @input="resetQuote" />
                              </v-col>
                              <v-col cols="12" sm="5">
                                <v-select
                                  v-model="partner.shirtSize"
                                  :items="event.shirtSizes"
                                  label="Unisex-T-Shirt-Grösse*"
                                  outlined
                                  dense
                                  hide-details="auto"
                                  :rules="[requiredRule]"
                                  @change="resetQuote" />
                              </v-col>
                            </v-row>
                          </div>
                        </v-expand-transition>
                      </section>

                      <section v-else key="time-step" class="form-section">
                        <div class="compact-race-summary">
                          <div>
                            <small>KATEGORIE</small>
                            <strong>{{ selectedCategory && selectedCategory.label }}</strong>
                          </div>
                          <span></span>
                          <div>
                            <small>START</small>
                            <strong>{{ selectedCategory && selectedCategory.startTime }}</strong>
                          </div>
                          <span></span>
                          <div>
                            <small>{{ isDouble ? "TEAM" : "STARTPLATZ" }}</small>
                            <strong>{{ isDouble ? "2 Personen" : "1 Person" }}</strong>
                          </div>
                        </div>

                        <div class="section-heading">
                          <span class="section-number">03</span>
                          <div>
                            <h3>Geschätzte Zielzeit</h3>
                            <p>
                              {{ isDouble ? "Wie lange benötigt ihr als Team?" : "Wie lange benötigst du voraussichtlich?" }}
                            </p>
                          </div>
                        </div>

                        <div class="time-input-wrap">
                          <v-icon size="23">mdi-timer-outline</v-icon>
                          <v-text-field
                            v-model.trim="estimatedTime"
                            label="Geschätzte Zeit (HH:MM)*"
                            placeholder="01:20"
                            outlined
                            dense
                            hide-details="auto"
                            :rules="[requiredRule, estimatedTimeRule]"
                            @input="resetQuote" />
                          <span>Std.</span>
                        </div>
                        <p class="field-hint">Beispiel: 01:20 entspricht 1 Stunde und 20 Minuten.</p>
                      </section>
                    </transition>

                    <div v-if="formStep === 3" class="terms-panel">
                      <v-checkbox
                        v-model="termsAccepted"
                        color="#f4d35e"
                        hide-details="auto"
                        :rules="[(value) => value || 'Bitte Bedingungen bestätigen']">
                        <template v-slot:label>
                          <span>
                            Ich bestätige die Kategorie und alle Angaben. Mir ist bekannt, dass keine reguläre Stornierung oder Rückerstattung
                            vorgesehen ist.
                          </span>
                        </template>
                      </v-checkbox>
                    </div>

                    <v-alert v-if="formError" type="error" text dense class="form-alert">
                      {{ formError }}
                    </v-alert>

                    <div v-if="formStep > 1" class="action-dock">
                      <v-btn text large class="secondary-button form-back-button" @click="goToFormStep(formStep - 1)">
                        <v-icon left size="19">mdi-arrow-left</v-icon>
                        Zurück
                      </v-btn>
                      <v-btn v-if="formStep === 2" class="primary-button" x-large @click="continueFromParticipants">
                        Weiter zur Zielzeit
                        <v-icon right size="20">mdi-arrow-right</v-icon>
                      </v-btn>
                      <v-btn
                        v-else
                        class="primary-button"
                        x-large
                        :loading="quoting"
                        :disabled="selectedCategory && selectedCategory.soldOut"
                        @click="requestQuote">
                        Anmeldung prüfen
                        <v-icon right size="20">mdi-arrow-right</v-icon>
                      </v-btn>
                    </div>
                  </v-form>
                </div>
              </v-window-item>

              <v-window-item :value="2">
                <div v-if="quote" class="flow-content review-content">
                  <div class="flow-heading review-heading">
                    <p class="eyebrow">Fast geschafft</p>
                    <h2>Alles bereit für den Start?</h2>
                    <p>Prüfe Team, Kategorie und Preis vor der verbindlichen Anmeldung.</p>
                  </div>

                  <div class="race-summary">
                    <div class="race-summary-main">
                      <span class="summary-icon">
                        <v-icon size="24">{{ categoryIcon(quote.category) }}</v-icon>
                      </span>
                      <div>
                        <small>KATEGORIE</small>
                        <strong>{{ quote.category.label }}</strong>
                      </div>
                    </div>
                    <div class="race-timing">
                      <div>
                        <small>BRIEFING</small>
                        <strong>{{ quote.category.briefingTime }}</strong>
                      </div>
                      <span />
                      <div>
                        <small>START</small>
                        <strong>{{ quote.category.startTime }}</strong>
                      </div>
                      <span />
                      <div>
                        <small>ZIELZEIT</small>
                        <strong>{{ quote.estimatedTime }}</strong>
                      </div>
                    </div>
                  </div>

                  <div class="review-label">TEILNEHMER</div>
                  <div class="review-participants">
                    <div v-for="(participant, index) in quote.participants" :key="participant.email" class="review-participant">
                      <span class="avatar-token">{{ participantInitials(participant) }}</span>
                      <div class="review-person-copy">
                        <small>TEILNEHMER {{ index + 1 }}</small>
                        <strong>{{ participant.firstName }} {{ participant.lastName }}</strong>
                        <span>{{ participant.email }}</span>
                      </div>
                      <div class="review-person-meta">
                        <span class="membership-pill" :class="participant.isMember ? 'member' : 'nonmember'">
                          {{ participant.isMember ? "MITGLIED" : "GAST" }}
                        </span>
                        <small>SHIRT {{ participant.shirtSize }}</small>
                      </div>
                    </div>
                  </div>

                  <div class="price-stage">
                    <div>
                      <small>GESAMTBETRAG</small>
                      <span v-if="isTrainer && quote.trainerDiscountApplied" class="regular-price">
                        Regulär CHF {{ Number(quote.regularFee).toFixed(2) }}
                      </span>
                      <strong>CHF {{ Number(quote.totalFee).toFixed(2) }}</strong>
                      <span v-if="isTrainer && quote.trainerDiscountApplied" class="trainer-saving">
                        {{ quote.discountPercent }} % Trainer-Rabatt · Du sparst CHF {{ Number(quote.discountAmount).toFixed(2) }}
                      </span>
                    </div>
                    <p>
                      Einmalige Zahlung für
                      {{ quote.participants.length === 2 ? "das ganze Team" : "deinen Startplatz" }}. Unisex-T-Shirt, Finisher-Patch, Fotos,
                      Verpflegung und Urkunde sind inklusive.
                    </p>
                  </div>

                  <v-alert v-if="formError" type="error" text dense class="form-alert">
                    {{ formError }}
                  </v-alert>

                  <div class="review-actions">
                    <v-btn text large class="secondary-button" @click="goToStep(1)">
                      <v-icon left size="19">mdi-arrow-left</v-icon>
                      Angaben ändern
                    </v-btn>
                    <v-btn class="primary-button" x-large :loading="registering" @click="createRegistration">
                      Verbindlich anmelden
                      <v-icon right size="20">mdi-lock-outline</v-icon>
                    </v-btn>
                  </div>
                </div>
                <div v-else class="window-loading">
                  <v-progress-circular indeterminate color="#f4d35e" size="44" />
                </div>
              </v-window-item>

              <v-window-item :value="3">
                <div class="flow-content payment-content">
                  <div class="payment-heading">
                    <span class="payment-lock">
                      <v-icon size="28">mdi-lock-outline</v-icon>
                    </span>
                    <p class="eyebrow">Sicher bezahlen</p>
                    <h2>Dein Startplatz wartet.</h2>
                    <div class="payment-price">CHF {{ Number(paymentTotal).toFixed(2) }}</div>
                    <p>Dein Platz ist während des aktiven Bezahlvorgangs reserviert.</p>
                  </div>

                  <div v-if="paymentLoading || paymentVerifying" class="payment-loading">
                    <div class="loader-orbit">
                      <v-progress-circular indeterminate color="#f4d35e" size="52" width="3" />
                    </div>
                    <strong>
                      {{ paymentVerifying ? "Zahlung wird bestätigt" : "Zahlungsmodul wird geladen" }}
                    </strong>
                    <span>Bitte dieses Fenster geöffnet lassen.</span>
                  </div>

                  <div v-show="!paymentLoading && !paymentVerifying" id="hyrox-simulation-adyen" class="adyen-container" />

                  <v-alert v-if="paymentError" type="error" text dense class="form-alert mt-5">
                    {{ paymentError }}
                  </v-alert>

                  <div class="payment-cancel-row">
                    <v-btn
                      text
                      large
                      class="secondary-button"
                      :loading="cancellingPayment"
                      :disabled="paymentLoading || paymentVerifying"
                      @click="cancelPendingPayment()">
                      <v-icon left size="19">mdi-close-circle-outline</v-icon>
                      Zahlung abbrechen
                    </v-btn>
                  </div>

                  <div class="payment-trust">
                    <span><v-icon size="17">mdi-shield-lock-outline</v-icon> Verschlüsselt</span>
                    <span><v-icon size="17">mdi-credit-card-check-outline</v-icon> Sicher via Adyen</span>
                  </div>
                </div>
              </v-window-item>

              <v-window-item :value="4">
                <div class="completion-state">
                  <div class="completion-mark success-mark">
                    <v-icon size="54">mdi-check</v-icon>
                  </div>
                  <p class="eyebrow">You are in</p>
                  <h2>Anmeldung erfolgreich!</h2>
                  <p v-if="successfulRegistration && successfulRegistration.registrationSource === 'admin_manual'">
                    Das Event-Team hat dich kostenlos als Instruktor angemeldet. Dein Startplatz ist bestätigt.
                  </p>
                  <p v-else>Die Zahlung ist bestätigt. Alle eingetragenen Teilnehmer erhalten ihre Bestätigung per E-Mail.</p>
                  <div v-if="successfulRegistration" class="completion-ticket">
                    <div>
                      <small>KATEGORIE</small>
                      <strong>{{ successfulRegistration.categoryLabel }}</strong>
                    </div>
                    <div>
                      <small>GESCHÄTZTE ZEIT</small>
                      <strong>{{ successfulRegistration.estimatedTime }} Std.</strong>
                    </div>
                  </div>
                  <v-btn class="primary-button" x-large @click="goHome">
                    Fertig
                    <v-icon right size="20">mdi-arrow-right</v-icon>
                  </v-btn>
                </div>
              </v-window-item>

              <v-window-item :value="5">
                <div class="completion-state">
                  <div class="completion-mark review-mark">
                    <v-icon size="48">mdi-clock-check-outline</v-icon>
                  </div>
                  <p class="eyebrow">Zahlung erhalten</p>
                  <h2>Deine Anmeldung wird kurz geprüft.</h2>
                  <p>Bitte führe keine zweite Zahlung aus. Das Team von St. Gallen West meldet sich bei dir, falls noch etwas benötigt wird.</p>
                  <div class="contact-pills centered-pills">
                    <a :href="`mailto:${event.contactEmail}`">
                      <v-icon size="17">mdi-email-outline</v-icon>
                      {{ event.contactEmail }}
                    </a>
                    <a :href="`tel:${event.contactPhone}`">
                      <v-icon size="17">mdi-phone-outline</v-icon>
                      {{ event.contactPhone }}
                    </a>
                  </div>
                  <v-btn class="primary-button mt-6" x-large @click="goHome"> Fertig </v-btn>
                </div>
              </v-window-item>
            </v-window>
          </v-card>
        </section>
      </template>
    </main>

    <v-dialog v-model="showSuccessDialog" max-width="540" content-class="hyrox-success-dialog" transition="dialog-bottom-transition">
      <v-card dark class="success-dialog-card">
        <button class="success-dialog-close" type="button" aria-label="Hinweis schliessen" @click="showSuccessDialog = false">
          <v-icon size="21">mdi-close</v-icon>
        </button>
        <div class="success-dialog-icon">
          <v-icon size="38">mdi-check</v-icon>
        </div>
        <p class="eyebrow">Anmeldung bestätigt</p>
        <h2>Schön, dass du dabei bist!</h2>
        <p>Weitere Infos, das Race-Briefing-Video und deine persönliche Startzeit erhältst du am Dienstag, den 1. September per Newsletter.</p>
        <v-btn class="primary-button success-dialog-button" x-large @click="showSuccessDialog = false">
          Alles klar
          <v-icon right size="20">mdi-arrow-right</v-icon>
        </v-btn>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"
import { mapState } from "vuex"

export default {
  name: "HyroxSimulationRegistration",
  data() {
    return {
      userService,
      loading: true,
      loadError: "",
      formError: "",
      formValid: false,
      event: null,
      step: 1,
      formStep: 1,
      formSteps: [
        { number: 1, label: "Kategorie" },
        { number: 2, label: "Teilnehmer" },
        { number: 3, label: "Zielzeit" },
      ],
      categoryKey: "",
      mainShirtSize: "",
      estimatedTime: "",
      termsAccepted: false,
      partner: {
        firstName: "",
        lastName: "",
        email: "",
        birthdate: "",
        shirtSize: "",
      },
      quote: null,
      quoting: false,
      registering: false,
      cancellingPayment: false,
      pendingRegistration: null,
      successfulRegistration: null,
      showSuccessDialog: false,
      reviewRegistration: null,
      registrationId: null,
      paymentTotal: 0,
      checkoutConfig: null,
      transactionId: null,
      paymentLoading: false,
      paymentVerifying: false,
      paymentError: "",
      checkout: null,
      adyenDropin: null,
      requiredRule: (value) => Boolean(value) || "Pflichtfeld",
      emailRule: (value) => /.+@.+\..+/.test(value || "") || "Ungültige E-Mail-Adresse",
      estimatedTimeRule: (value) => /^(\d{1,2}):([0-5]\d)$/.test(value || "") || "Bitte im Format HH:MM eingeben",
    }
  },
  computed: {
    ...mapState("userStore", ["user"]),
    isTrainer() {
      return this.userService.hasRight("gf_cp_instructor")
    },
    availableCategories() {
      if (!this.event) return []
      return this.event.categories
        .filter((category) => category.enabled)
        .map((category) => ({
          ...category,
          selectionLabel: `${category.label} · ${category.startTime} Uhr${category.soldOut ? " · AUSGEBUCHT" : ""}`,
          disabled: category.soldOut,
        }))
    },
    selectedCategory() {
      if (!this.event) return null
      return this.event.categories.find((category) => category.key === this.categoryKey) || null
    },
    isDouble() {
      return Boolean(this.selectedCategory && this.selectedCategory.type === "double")
    },
    eventDateParts() {
      const date = this.event && this.event.eventDate ? new Date(this.event.eventDate) : null
      if (!date || Number.isNaN(date.getTime())) {
        return { day: "--", month: "---", year: "----" }
      }
      return {
        day: new Intl.DateTimeFormat("de-CH", { day: "2-digit" }).format(date),
        month: new Intl.DateTimeFormat("de-CH", { month: "short" }).format(date).replace(".", "").toUpperCase(),
        year: new Intl.DateTimeFormat("de-CH", { year: "numeric" }).format(date),
      }
    },
    userInitials() {
      const firstName = this.user && this.user.vorname
      const lastName = this.user && this.user.nachname
      return `${String(firstName || "").charAt(0)}${String(lastName || "").charAt(0)}`.toUpperCase() || "DU"
    },
    formStepMeta() {
      return {
        1: {
          title: "Wähle deine Kategorie.",
          description: "Single oder Double – nach deiner Auswahl geht es direkt mit den Teilnehmerdaten weiter.",
        },
        2: {
          title: this.isDouble ? "Stellt euer Team zusammen." : "Dein Startplatz.",
          description: this.isDouble
            ? "Ergänze die Unisex-Shirt-Grössen und die Angaben deines Double-Partners."
            : "Kontrolliere deine App-Daten und wähle deine Unisex-Shirt-Grösse.",
        },
        3: {
          title: this.isDouble ? "Was ist eure Zielzeit?" : "Was ist deine Zielzeit?",
          description: "Eine realistische Schätzung hilft uns bei der optimalen Einteilung der Startwellen.",
        },
      }[this.formStep]
    },
    formProgressPercent() {
      return ((this.formStep - 1) / 2) * 100
    },
    overallProgressStep() {
      if (this.step === 1) return this.formStep
      if (this.step === 2) return 4
      return 5
    },
    overallProgressPercent() {
      return ((this.overallProgressStep - 1) / 4) * 100
    },
  },
  async created() {
    await this.loadEvent()

    const query = this.$route.query
    if (this.pendingRegistration && query.status !== "checkPayment") {
      await this.cancelPendingPayment({
        notification: "Die unterbrochene Zahlung wurde verworfen. Du kannst die Anmeldung neu starten.",
      })
      return
    }
    if (query.status === "checkPayment" && query.txId) {
      if (this.pendingRegistration && this.pendingRegistration.id) {
        this.registrationId = this.pendingRegistration.id
      }
      this.pendingRegistration = null
      this.transactionId = query.txId
      this.step = 3
      this.paymentVerifying = true
      await this.verifyPayment(query.txId, query.redirectResult)
    }
  },
  beforeDestroy() {
    if (this.adyenDropin) {
      try {
        this.adyenDropin.unmount()
      } catch (error) {
        console.error(error)
      }
    }
  },
  methods: {
    goBack() {
      if (this.step === 3 && (this.registrationId || this.pendingRegistration)) {
        this.cancelPendingPayment()
        return
      }
      if (this.step === 2) {
        this.goToStep(1)
        return
      }
      if (this.step === 1 && this.formStep > 1) {
        this.goToFormStep(this.formStep - 1)
        return
      }
      this.$router.go(-1)
    },
    goHome() {
      this.$router.push("/home")
    },
    scrollToFlowCard() {
      this.$nextTick(() => {
        const flowCard = this.$refs.flowCard
        const element = flowCard && flowCard.$el ? flowCard.$el : flowCard
        if (element && typeof element.scrollIntoView === "function") {
          element.scrollIntoView({ behavior: "smooth", block: "start" })
        }
      })
    },
    goToStep(targetStep) {
      this.step = targetStep
      this.scrollToFlowCard()
    },
    goToFormStep(targetStep) {
      this.formStep = Math.min(3, Math.max(1, Number(targetStep) || 1))
      this.formError = ""
      this.scrollToFlowCard()
    },
    formatDate(value) {
      if (!value) return ""
      return new Date(value).toLocaleDateString("de-CH", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    },
    formatDateTime(value) {
      if (!value) return ""
      return new Date(value).toLocaleString("de-CH", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    },
    capacityText(category) {
      if (category.soldOut) return "Diese Kategorie ist ausgebucht."
      if (category.type === "double") {
        return `Noch ${category.remainingRegistrations} Teamplätze verfügbar.`
      }
      return `Noch ${category.remainingParticipants} Plätze verfügbar.`
    },
    categoryAvailability(category) {
      if (category.soldOut) return "AUSGEBUCHT"
      const remaining = category.type === "double" ? category.remainingRegistrations : category.remainingParticipants
      return `${remaining} ${category.type === "double" ? "TEAMS" : "PLÄTZE"}`
    },
    categoryIcon(category) {
      if (!category) return "mdi-run-fast"
      if (category.composition === "mixed") return "mdi-gender-male-female"
      return category.type === "double" ? "mdi-account-multiple" : "mdi-run-fast"
    },
    selectCategory(category) {
      if (!category || category.soldOut) return
      this.categoryKey = category.key
      this.resetQuote()
      this.goToFormStep(2)
    },
    participantInitials(participant) {
      if (!participant) return "–"
      return `${String(participant.firstName || "").charAt(0)}${String(participant.lastName || "").charAt(0)}`.toUpperCase()
    },
    resetQuote() {
      this.quote = null
      this.formError = ""
    },
    participantValidationMessage() {
      if (!this.mainShirtSize) {
        return "Bitte wähle deine Unisex-T-Shirt-Grösse."
      }
      if (!this.isDouble) return ""
      if (!this.partner.firstName || !this.partner.lastName) {
        return "Bitte ergänze Vor- und Nachname deines Double-Partners."
      }
      if (this.emailRule(this.partner.email) !== true) {
        return "Bitte gib eine gültige E-Mail-Adresse für deinen Double-Partner ein."
      }
      if (!this.partner.birthdate) {
        return "Bitte ergänze das Geburtsdatum deines Double-Partners."
      }
      if (!this.partner.shirtSize) {
        return "Bitte wähle die Unisex-T-Shirt-Grösse deines Double-Partners."
      }
      return ""
    },
    continueFromParticipants() {
      this.formError = ""
      const formIsValid = !this.$refs.registrationForm || this.$refs.registrationForm.validate()
      const validationMessage = this.participantValidationMessage()
      if (!formIsValid || validationMessage) {
        this.formError = validationMessage || "Bitte prüfe die markierten Pflichtfelder."
        return
      }
      this.goToFormStep(3)
    },
    payload() {
      return {
        categoryKey: this.categoryKey,
        mainShirtSize: this.mainShirtSize,
        estimatedTime: this.estimatedTime,
        partnerFirstName: this.isDouble ? this.partner.firstName : "",
        partnerLastName: this.isDouble ? this.partner.lastName : "",
        partnerEmail: this.isDouble ? this.partner.email : "",
        partnerBirthdate: this.isDouble ? this.partner.birthdate : "",
        partnerShirtSize: this.isDouble ? this.partner.shirtSize : "",
        termsAccepted: this.termsAccepted,
      }
    },
    async loadEvent() {
      this.loading = true
      this.loadError = ""
      try {
        const response = await api.hyroxSimulation.fetchAvailability()
        this.event = response.event
        if (response.myRegistration) {
          if (response.myRegistration.status === "active") {
            this.successfulRegistration = response.myRegistration
            this.step = 4
          } else if (response.myRegistration.status === "payment_review") {
            this.reviewRegistration = response.myRegistration
            this.step = 5
          } else if (response.myRegistration.status === "pending_payment") {
            this.pendingRegistration = response.myRegistration
          }
        }
      } catch (error) {
        this.loadError = error.response?.data || "Die Eventdaten konnten nicht geladen werden."
      } finally {
        this.loading = false
      }
    },
    async requestQuote() {
      this.formError = ""
      if (!this.selectedCategory || this.selectedCategory.soldOut) {
        this.goToFormStep(1)
        this.formError = "Bitte wähle eine verfügbare Kategorie."
        return
      }
      const participantError = this.participantValidationMessage()
      if (participantError) {
        this.goToFormStep(2)
        this.formError = participantError
        return
      }
      if (!this.$refs.registrationForm.validate()) {
        this.formError = "Bitte prüfe die markierten Pflichtfelder."
        return
      }

      this.quoting = true
      try {
        this.quote = await api.hyroxSimulation.quote(this.payload())
        this.goToStep(2)
      } catch (error) {
        this.formError = error.response?.data || "Mitgliedschaften und Preis konnten nicht geprüft werden."
      } finally {
        this.quoting = false
      }
    },
    async createRegistration() {
      this.formError = ""
      this.registering = true
      try {
        const response = await api.hyroxSimulation.register({
          ...this.payload(),
          returnUrl: `${window.location.origin}/hyrox-simulation?status=checkPayment`,
        })
        await this.openPaymentSession(response)
      } catch (error) {
        this.formError = error.response?.data || "Die Anmeldung konnte nicht gestartet werden."
      } finally {
        this.registering = false
      }
    },
    async cancelPendingPayment(options = {}) {
      if (this.cancellingPayment || this.paymentVerifying) return

      const registrationId = this.registrationId || (this.pendingRegistration && this.pendingRegistration.id)
      if (!registrationId) return

      const redirectHome = Boolean(options.redirectHome)
      const notification = options.notification || ""
      this.cancellingPayment = true
      this.paymentError = ""

      try {
        await api.hyroxSimulation.cancelPending(registrationId)

        if (this.adyenDropin) {
          try {
            this.adyenDropin.unmount()
          } catch (error) {
            console.error("HYROX Simulation checkout unmount failed:", error)
          }
        }

        this.adyenDropin = null
        this.checkout = null
        this.checkoutConfig = null
        this.transactionId = null
        this.registrationId = null
        this.pendingRegistration = null
        this.paymentTotal = 0
        this.step = this.quote ? 2 : 1
        if (!this.quote) this.formStep = 1

        if (redirectHome) {
          await this.$router.push("/home")
          return
        }

        if (this.$route.query.status) {
          await this.$router.replace({ path: "/hyrox-simulation" })
        }
        await this.loadEvent()
        this.$toast.info(notification || "Die alte Reservation wurde freigegeben. Du kannst neu starten.")
      } catch (error) {
        this.paymentError = error.response?.data || "Die Zahlungsreservation konnte nicht aufgehoben werden."
        await this.loadEvent()
      } finally {
        this.cancellingPayment = false
      }
    },
    async openPaymentSession(response) {
      this.registrationId = response.registrationId
      this.paymentTotal = response.totalFee
      this.checkoutConfig = response.checkoutConfig
      this.transactionId = response.transactionId
      this.pendingRegistration = null
      this.goToStep(3)
      await this.$nextTick()
      await this.initAdyenCheckout()
    },
    async initAdyenCheckout() {
      this.paymentLoading = true
      this.paymentError = ""

      if (window.flutter_inappwebview && typeof window.flutter_inappwebview.callHandler === "function") {
        try {
          await window.flutter_inappwebview.callHandler("allowAllRedirects")
        } catch (error) {
          console.warn("HYROX Simulation could not enable payment redirects:", error)
        }
      }

      if (!document.getElementById("adyen-css")) {
        const link = document.createElement("link")
        link.id = "adyen-css"
        link.rel = "stylesheet"
        link.href = "https://checkoutshopper-live.adyen.com/checkoutshopper/sdk/v5.57.0/adyen.css"
        document.head.appendChild(link)
      }

      try {
        const AdyenCheckout = (await import("@adyen/adyen-web")).default
        this.checkout = await AdyenCheckout({
          environment: this.checkoutConfig.environment || "live",
          clientKey: this.checkoutConfig.clientKey,
          locale: "de-CH",
          session: this.checkoutConfig.session,
          onPaymentCompleted: async (result) => {
            if (["Authorised", "Received", "Pending"].includes(result.resultCode)) {
              await this.verifyPayment(this.transactionId, null, result.pspReference)
            } else {
              await this.cancelPendingPayment({
                notification: "Die Zahlung wurde nicht abgeschlossen. Die Reservation wurde freigegeben.",
              })
            }
          },
          onError: async (error) => {
            console.error("HYROX Simulation Adyen error:", error)
            await this.cancelPendingPayment({
              notification: "Die Zahlung wurde abgebrochen. Die Reservation wurde freigegeben.",
            })
          },
          paymentMethodsConfiguration: {
            card: {
              hasHolderName: true,
              holderNameRequired: true,
              billingAddressRequired: false,
            },
          },
        })
        this.adyenDropin = this.checkout.create("dropin").mount("#hyrox-simulation-adyen")
      } catch (error) {
        console.error("HYROX Simulation checkout init failed:", error)
        await this.cancelPendingPayment({
          notification: "Das Zahlungsmodul konnte nicht geladen werden. Bitte starte die Anmeldung neu.",
        })
      } finally {
        this.paymentLoading = false
      }
    },
    async verifyPayment(transactionId, redirectResult = null, pspReference = null) {
      this.paymentVerifying = true
      this.paymentError = ""

      try {
        let response = null
        for (let attempt = 0; attempt <= 30; attempt += 1) {
          response = await api.hyroxSimulation.payment.verify(transactionId, redirectResult, pspReference)
          if (response !== "webhookMissing") break
          if (attempt === 30) {
            throw new Error("Die Zahlungsbestätigung dauert zu lange.")
          }
          await new Promise((resolve) => setTimeout(resolve, 2500))
        }

        if (response && response.status === "captured") {
          await this.loadEvent()
          this.pendingRegistration = null
          if (this.reviewRegistration) {
            this.step = 5
            this.$toast.info("Zahlung erhalten – Anmeldung wird geprüft.")
          } else {
            this.step = 4
            this.showSuccessDialog = true
            this.$toast.success("Zahlung und Anmeldung erfolgreich!")
          }
        } else {
          this.paymentError = response?.message || "Die Zahlung konnte nicht bestätigt werden."
        }
      } catch (error) {
        console.error("HYROX Simulation payment verification failed:", error)
        this.paymentError = error.response?.data || error.message || "Die Zahlung konnte nicht bestätigt werden."
      } finally {
        this.paymentVerifying = false
      }
    },
  },
}
</script>

<style scoped>
.simulation-shell {
  --ink: #f8f7f2;
  --muted: #a6a7aa;
  --muted-strong: #c8c8c5;
  --surface: rgba(22, 23, 24, 0.94);
  --surface-soft: rgba(255, 255, 255, 0.045);
  --stroke: rgba(255, 255, 255, 0.1);
  --accent: #f4d35e;
  --accent-bright: #ffe47f;
  --accent-ink: #17160f;
  min-height: 100vh;
  font-size: 1rem;
  line-height: 1.5;
  overflow-x: hidden;
  position: relative;
  color: var(--ink);
  background: radial-gradient(circle at 15% -10%, rgba(244, 211, 94, 0.13), transparent 32rem),
    radial-gradient(circle at 100% 40%, rgba(93, 139, 255, 0.08), transparent 34rem), #0a0b0c;
  font-family: Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

.ambient {
  position: fixed;
  z-index: 0;
  width: 420px;
  height: 420px;
  border-radius: 999px;
  filter: blur(110px);
  opacity: 0.11;
  pointer-events: none;
}

.ambient-one {
  top: 6vh;
  right: -180px;
  background: var(--accent);
}

.ambient-two {
  bottom: -240px;
  left: -180px;
  background: #4b6fff;
}

.background-grid {
  position: fixed;
  inset: 0;
  z-index: 0;
  opacity: 0.04;
  pointer-events: none;
  background-image: linear-gradient(rgba(255, 255, 255, 0.5) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.5) 1px, transparent 1px);
  background-size: 52px 52px;
  mask-image: linear-gradient(to bottom, black, transparent 78%);
}

.registration-topbar {
  position: sticky;
  top: 0;
  z-index: 30;
  display: grid;
  grid-template-columns: minmax(190px, 1fr) auto minmax(190px, 1fr);
  min-height: 72px;
  padding: 0 max(22px, calc((100vw - 1240px) / 2));
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  background: rgba(10, 11, 12, 0.76);
  backdrop-filter: blur(22px) saturate(140%);
  -webkit-backdrop-filter: blur(22px) saturate(140%);
}

.nav-back {
  display: grid;
  grid-column: 1;
  grid-row: 1;
  width: 44px;
  height: 44px;
  padding: 0;
  justify-self: start;
  place-items: center;
  color: var(--accent);
  border: 1px solid rgba(244, 211, 94, 0.3);
  border-radius: 14px;
  background: rgba(244, 211, 94, 0.065);
  box-shadow: none;
  cursor: pointer;
  transition: transform 180ms ease, border-color 180ms ease, background 180ms ease;
}

.nav-back .v-icon {
  color: var(--accent) !important;
}

.nav-back:hover {
  border-color: rgba(244, 211, 94, 0.5);
  background: rgba(244, 211, 94, 0.1);
  transform: translateX(-2px);
}

.nav-back:focus-visible,
.category-option:focus-visible,
.contact-pills a:focus-visible {
  outline: 3px solid rgba(244, 211, 94, 0.38);
  outline-offset: 3px;
}

.brand-lockup {
  display: flex;
  grid-column: 1;
  grid-row: 1;
  margin-left: 58px;
  align-items: center;
  gap: 10px;
}

.brand-mark {
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  color: var(--accent-ink);
  border-radius: 11px;
  background: var(--accent);
  font-size: 0.75rem;
  font-weight: 950;
  letter-spacing: -0.025rem;
  box-shadow: 0 8px 22px rgba(244, 211, 94, 0.2);
}

.brand-copy {
  font-size: 0.75rem;
  font-weight: 900;
  line-height: 1.08;
  letter-spacing: 0.084375rem;
}

.top-progress {
  position: relative;
  display: flex;
  grid-column: 2;
  grid-row: 1;
  width: 330px;
  justify-self: center;
  justify-content: space-between;
}

.mobile-top-progress {
  display: none;
}

.progress-track {
  position: absolute;
  top: 14px;
  right: 38px;
  left: 38px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  height: 2px;
  background: rgba(255, 255, 255, 0.1);
}

.progress-track span {
  background: transparent;
  transition: background 280ms ease;
}

.progress-track span.filled {
  background: var(--accent);
}

.progress-node {
  position: relative;
  z-index: 1;
  display: flex;
  width: 72px;
  align-items: center;
  flex-direction: column;
  gap: 5px;
  color: #7d7f82;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.01875rem;
}

.progress-node > span,
.progress-node > .v-icon {
  display: grid;
  width: 30px;
  height: 30px;
  place-items: center;
  color: #929397;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  background: #111214;
  font-size: 0.75rem;
}

.progress-node.active,
.progress-node.complete {
  color: var(--ink);
}

.progress-node.active > span {
  color: var(--accent-ink);
  border-color: var(--accent);
  background: var(--accent);
  box-shadow: 0 0 0 5px rgba(244, 211, 94, 0.1);
}

.progress-node.complete > .v-icon {
  color: var(--accent-ink);
  border-color: var(--accent);
  background: var(--accent);
}

.progress-node small {
  font-size: 0.75rem;
}

.mobile-progress-track {
  position: absolute;
  top: 13px;
  right: 14px;
  left: 14px;
  height: 2px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.1);
}

.mobile-progress-track i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--accent);
  transition: width 300ms cubic-bezier(0.22, 1, 0.36, 1);
}

.mobile-progress-node {
  position: relative;
  z-index: 1;
  display: grid;
  width: 28px;
  height: 28px;
  place-items: center;
  color: #8b8d90;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 999px;
  background: #111214;
  font-size: 0.75rem;
  font-weight: 900;
  transition: color 200ms ease, border-color 200ms ease, background 200ms ease, box-shadow 200ms ease;
}

.mobile-progress-node.active,
.mobile-progress-node.complete {
  color: var(--accent-ink);
  border-color: var(--accent);
  background: var(--accent);
}

.mobile-progress-node.active {
  box-shadow: 0 0 0 4px rgba(244, 211, 94, 0.09);
}

.mobile-progress-node .v-icon {
  color: var(--accent-ink);
}

.secure-label {
  display: flex;
  grid-column: 3;
  grid-row: 1;
  justify-self: end;
  align-items: center;
  gap: 7px;
  color: #929397;
  font-size: 0.875rem;
  font-weight: 700;
}

.secure-label .v-icon {
  color: var(--accent);
}

.simulation-container {
  position: relative;
  z-index: 1;
  width: min(1240px, calc(100% - 40px));
  margin: 0 auto;
  padding: 34px 0 80px;
}

.loading-stage,
.window-loading {
  display: flex;
  min-height: 380px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 9px;
  color: var(--muted);
}

.loading-stage strong,
.payment-loading strong {
  margin-top: 12px;
  color: var(--ink);
  font-size: 1.0625rem;
}

.loading-stage span,
.payment-loading span {
  font-size: 0.875rem;
}

.loader-orbit {
  display: grid;
  width: 76px;
  height: 76px;
  place-items: center;
  border: 1px solid rgba(244, 211, 94, 0.14);
  border-radius: 999px;
  background: rgba(244, 211, 94, 0.035);
}

.event-stage {
  position: relative;
  display: grid;
  min-height: 245px;
  padding: 36px 42px;
  overflow: hidden;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 30px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 30px;
  background: linear-gradient(118deg, rgba(30, 31, 32, 0.96), rgba(17, 18, 19, 0.9)), #171819;
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.3);
}

.event-stage::before {
  position: absolute;
  top: -70%;
  right: 4%;
  width: 420px;
  height: 420px;
  border: 1px solid rgba(244, 211, 94, 0.15);
  border-radius: 999px;
  content: "";
  box-shadow: 0 0 0 50px rgba(244, 211, 94, 0.018), 0 0 0 100px rgba(244, 211, 94, 0.012);
}

.event-date-block {
  position: relative;
  display: flex;
  width: 112px;
  height: 142px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  border: 1px solid rgba(244, 211, 94, 0.38);
  border-radius: 22px;
  background: rgba(244, 211, 94, 0.055);
}

.event-date-block strong {
  font-size: 3rem;
  font-weight: 950;
  line-height: 0.9;
  letter-spacing: -0.1875rem;
}

.event-date-block span {
  margin-top: 9px;
  color: var(--accent);
  font-size: 0.875rem;
  font-weight: 950;
  letter-spacing: 0.14375rem;
}

.event-date-block small {
  margin-top: 2px;
  color: #7f8083;
  font-size: 0.75rem;
  font-weight: 700;
}

.event-intro {
  position: relative;
  z-index: 1;
  max-width: 750px;
}

.event-kicker,
.eyebrow {
  margin: 0 0 11px;
  color: var(--accent);
  font-size: 0.75rem;
  font-weight: 950;
  letter-spacing: 0.1125rem;
  text-transform: uppercase;
}

.event-kicker {
  display: flex;
  align-items: center;
  gap: 8px;
}

.live-dot {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: var(--accent);
  box-shadow: 0 0 0 5px rgba(244, 211, 94, 0.1);
}

.event-intro h1 {
  display: flex;
  max-width: 760px;
  margin: 0;
  align-items: flex-start;
  flex-direction: column;
  font-size: 3.375rem;
  font-weight: 950;
  font-style: italic;
  line-height: 0.9;
  letter-spacing: -0.15rem;
  text-transform: uppercase;
}

.event-title span:last-child {
  position: relative;
}

.event-title span:last-child::after {
  position: absolute;
  right: -17px;
  bottom: 5px;
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: var(--accent);
  content: "";
  box-shadow: 0 0 0 4px rgba(244, 211, 94, 0.1);
}

.event-intro > p {
  max-width: 680px;
  margin: 16px 0 20px;
  color: var(--muted);
  font-size: 1rem;
  line-height: 1.65;
}

.event-facts {
  display: flex;
  flex-wrap: wrap;
  gap: 9px;
}

.event-facts span {
  display: inline-flex;
  min-height: 34px;
  padding: 0 12px;
  align-items: center;
  gap: 7px;
  color: var(--muted-strong);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.035);
  font-size: 0.875rem;
  font-weight: 700;
}

.event-facts .v-icon {
  color: var(--accent);
}

.event-badge {
  position: relative;
  z-index: 1;
  display: flex;
  width: 94px;
  height: 120px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: var(--accent-ink);
  border-radius: 20px;
  background: var(--accent);
  transform: rotate(3deg);
  box-shadow: 0 22px 50px rgba(244, 211, 94, 0.16);
}

.event-badge span,
.event-badge small {
  font-size: 0.75rem;
  font-weight: 950;
  letter-spacing: 0.09375rem;
}

.event-badge strong {
  font-size: 2.625rem;
  font-weight: 950;
  line-height: 0.95;
  letter-spacing: -0.125rem;
}

.flow-layout {
  display: grid;
  margin-top: 24px;
  grid-template-columns: 286px minmax(0, 1fr);
  align-items: start;
  gap: 24px;
}

.event-rail {
  position: sticky;
  top: 96px;
}

.rail-card,
.flow-card,
.state-card {
  border: 1px solid var(--stroke) !important;
  background: var(--surface) !important;
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.22) !important;
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.rail-card {
  padding: 22px;
  border-radius: 22px;
}

.rail-heading {
  display: flex;
  margin-bottom: 24px;
  align-items: center;
  justify-content: space-between;
  color: var(--ink);
  font-size: 0.75rem;
  font-weight: 950;
  letter-spacing: 0.075rem;
}

.rail-detail {
  display: grid;
  margin-top: 17px;
  grid-template-columns: 24px 1fr;
  gap: 10px;
}

.rail-detail > .v-icon {
  margin-top: 1px;
  color: #77797c;
}

.rail-detail div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.rail-detail small,
.rail-price small,
.race-summary small,
.review-label,
.review-participant small,
.price-stage small,
.completion-ticket small {
  color: #727477;
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.06875rem;
  text-transform: uppercase;
}

.rail-detail strong {
  margin-top: 2px;
  color: var(--muted-strong);
  font-size: 0.875rem;
  line-height: 1.35;
}

.rail-detail span {
  margin-top: 2px;
  color: #797b7e;
  font-size: 0.875rem;
  overflow-wrap: anywhere;
}

.rail-divider {
  height: 1px;
  margin: 22px 0;
  background: rgba(255, 255, 255, 0.07);
}

.rail-price {
  display: flex;
  flex-direction: column;
}

.rail-price strong {
  margin: 3px 0;
  color: var(--accent);
  font-size: 1.75rem;
  font-weight: 950;
  letter-spacing: -0.075rem;
}

.rail-price span {
  color: #77797c;
  font-size: 0.875rem;
  line-height: 1.45;
}

.mini-schedule-title {
  margin-bottom: 11px;
  color: #77797c;
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.06875rem;
  text-transform: uppercase;
}

.mini-schedule-row {
  display: flex;
  min-height: 28px;
  padding: 0 7px;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: #858689;
  border-radius: 7px;
  font-size: 0.875rem;
  transition: color 180ms ease, background 180ms ease;
}

.mini-schedule-row.selected {
  color: var(--ink);
  background: rgba(244, 211, 94, 0.09);
}

.mini-schedule-row strong {
  color: var(--muted-strong);
  font-size: 0.875rem;
}

.flow-card {
  overflow: hidden;
  border-radius: 28px !important;
}

.flow-content {
  padding: 42px;
}

.flow-heading {
  max-width: 650px;
  margin-bottom: 38px;
}

.flow-heading h2,
.state-card h1,
.state-card h2,
.completion-state h2,
.payment-heading h2 {
  margin: 0;
  color: var(--ink);
  font-size: 2.4375rem;
  font-weight: 950;
  line-height: 1.05;
  letter-spacing: -0.0875rem;
}

.flow-heading > p:last-child,
.state-card > p:not(.eyebrow),
.completion-state > p:not(.eyebrow),
.payment-heading > p:last-child {
  margin: 12px 0 0;
  color: var(--muted);
  font-size: 1rem;
  line-height: 1.65;
}

.trainer-benefit {
  display: grid;
  margin: -12px 0 38px;
  padding: 18px;
  grid-template-columns: 48px minmax(0, 1fr);
  align-items: start;
  gap: 14px;
  border: 1px solid rgba(244, 211, 94, 0.24);
  border-radius: 18px;
  background: linear-gradient(125deg, rgba(244, 211, 94, 0.11), rgba(244, 211, 94, 0.035)), rgba(255, 255, 255, 0.02);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
}

.trainer-benefit-icon {
  display: grid;
  width: 48px;
  height: 48px;
  place-items: center;
  color: var(--accent-ink);
  border-radius: 15px;
  background: var(--accent);
  box-shadow: 0 10px 24px rgba(244, 211, 94, 0.14);
}

.trainer-benefit-icon .v-icon {
  color: var(--accent-ink);
}

.trainer-benefit-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 7px 12px;
}

.trainer-benefit-heading strong {
  color: var(--ink);
  font-size: 1rem;
  font-weight: 900;
  line-height: 1.35;
}

.trainer-benefit-heading span {
  padding: 5px 9px;
  color: var(--accent);
  border: 1px solid rgba(244, 211, 94, 0.2);
  border-radius: 999px;
  background: rgba(244, 211, 94, 0.07);
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.01875rem;
}

.trainer-benefit p {
  margin: 7px 0 0;
  color: var(--muted-strong);
  font-size: 0.875rem;
  line-height: 1.55;
}

.form-stepper {
  position: relative;
  display: flex;
  max-width: 560px;
  margin: -12px 0 38px;
  justify-content: space-between;
}

.form-step-track {
  position: absolute;
  top: 14px;
  right: 42px;
  left: 42px;
  height: 2px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.08);
}

.form-step-track i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--accent);
  transition: width 320ms cubic-bezier(0.22, 1, 0.36, 1);
}

.form-step-node {
  position: relative;
  z-index: 1;
  display: flex;
  width: 84px;
  align-items: center;
  flex-direction: column;
  gap: 7px;
  color: #686a6d;
}

.form-step-node > span {
  display: grid;
  width: 30px;
  height: 30px;
  place-items: center;
  color: #85878a;
  border: 1px solid rgba(255, 255, 255, 0.11);
  border-radius: 999px;
  background: #151617;
  font-size: 0.75rem;
  font-weight: 900;
  transition: color 220ms ease, border-color 220ms ease, background 220ms ease, box-shadow 220ms ease;
}

.form-step-node small {
  font-size: 0.75rem;
  font-weight: 800;
}

.form-step-node.active,
.form-step-node.complete {
  color: var(--ink);
}

.form-step-node.active > span,
.form-step-node.complete > span {
  color: var(--accent-ink);
  border-color: var(--accent);
  background: var(--accent);
}

.form-step-node.active > span {
  box-shadow: 0 0 0 5px rgba(244, 211, 94, 0.09);
}

.form-step-node.complete .v-icon {
  color: var(--accent-ink);
}

.event-guidance {
  display: grid;
  margin: 0 0 34px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.guidance-item {
  display: grid;
  min-width: 0;
  padding: 16px;
  grid-template-columns: 42px minmax(0, 1fr);
  align-items: start;
  gap: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.03);
}

.guidance-icon {
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  color: var(--accent);
  border: 1px solid rgba(244, 211, 94, 0.2);
  border-radius: 13px;
  background: rgba(244, 211, 94, 0.065);
}

.guidance-icon .v-icon {
  color: var(--accent);
}

.guidance-item strong {
  display: block;
  color: var(--ink);
  font-size: 1rem;
  font-weight: 900;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.guidance-item p {
  margin: 5px 0 0;
  color: #85878a;
  font-size: 0.875rem;
  line-height: 1.55;
  overflow-wrap: anywhere;
}

.form-section + .form-section {
  margin-top: 42px;
  padding-top: 42px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
}

.section-heading {
  display: flex;
  margin-bottom: 19px;
  align-items: flex-start;
  gap: 13px;
}

.section-number {
  display: grid;
  width: 32px;
  height: 32px;
  flex: 0 0 auto;
  place-items: center;
  color: var(--accent-ink);
  border-radius: 10px;
  background: var(--accent);
  font-size: 0.75rem;
  font-weight: 950;
}

.section-heading h3 {
  margin: 0;
  color: var(--ink);
  font-size: 1.125rem;
  font-weight: 900;
}

.section-heading p {
  margin: 3px 0 0;
  color: #7f8184;
  font-size: 0.875rem;
  line-height: 1.5;
}

.category-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.category-option {
  position: relative;
  display: grid;
  min-height: 92px;
  padding: 15px 15px 15px 14px;
  grid-template-columns: 42px minmax(0, 1fr);
  align-items: center;
  column-gap: 12px;
  color: var(--ink);
  text-align: left;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.032);
  cursor: pointer;
  transition: transform 180ms ease, border-color 180ms ease, background 180ms ease, box-shadow 180ms ease;
}

.category-option:hover:not(:disabled) {
  border-color: rgba(244, 211, 94, 0.38);
  background: rgba(244, 211, 94, 0.055);
  transform: translateY(-2px);
}

.category-option.selected {
  border-color: rgba(244, 211, 94, 0.7);
  background: linear-gradient(135deg, rgba(244, 211, 94, 0.11), rgba(244, 211, 94, 0.035));
  box-shadow: inset 0 0 0 1px rgba(244, 211, 94, 0.1);
}

.category-option.soldout {
  cursor: not-allowed;
  opacity: 0.42;
}

.category-icon,
.summary-icon {
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  color: var(--accent);
  border: 1px solid rgba(244, 211, 94, 0.2);
  border-radius: 13px;
  background: rgba(244, 211, 94, 0.065);
}

.category-icon .v-icon,
.summary-icon .v-icon {
  color: var(--accent);
}

.category-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.category-copy strong {
  font-size: 1rem;
  font-weight: 900;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.category-copy small {
  margin-top: 3px;
  color: #7d7f82;
  font-size: 0.875rem;
  font-weight: 700;
}

.availability {
  grid-column: 2;
  width: max-content;
  margin-top: -9px;
  padding: 3px 7px;
  color: #7ecf9a;
  border-radius: 999px;
  background: rgba(59, 173, 99, 0.09);
  font-size: 0.75rem;
  font-weight: 950;
  letter-spacing: 0.040625rem;
}

.availability.scarce {
  color: #ffb36b;
  background: rgba(255, 154, 60, 0.09);
}

.selected-check {
  position: absolute;
  top: 9px;
  right: 9px;
  color: var(--accent) !important;
}

.inline-note {
  display: flex;
  margin-top: 11px;
  padding: 10px 12px;
  align-items: center;
  gap: 8px;
  color: #c5c6c7;
  border: 1px solid rgba(122, 162, 255, 0.18);
  border-radius: 11px;
  background: rgba(91, 134, 239, 0.065);
  font-size: 0.875rem;
  line-height: 1.5;
}

.inline-note .v-icon {
  color: #8baeff;
}

.auto-advance-note {
  display: flex;
  margin-top: 14px;
  align-items: center;
  justify-content: center;
  gap: 7px;
  color: #717377;
  font-size: 0.875rem;
}

.auto-advance-note .v-icon {
  color: #8b8d90;
}

.current-selection {
  display: grid;
  width: 100%;
  min-height: 66px;
  margin-bottom: 28px;
  padding: 11px 13px;
  grid-template-columns: 42px minmax(0, 1fr) auto;
  align-items: center;
  gap: 11px;
  color: var(--ink);
  text-align: left;
  border: 1px solid rgba(244, 211, 94, 0.22);
  border-radius: 15px;
  background: rgba(244, 211, 94, 0.04);
  cursor: pointer;
  transition: border-color 180ms ease, background 180ms ease;
}

.current-selection:hover {
  border-color: rgba(244, 211, 94, 0.45);
  background: rgba(244, 211, 94, 0.065);
}

.current-selection > span:nth-child(2) {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.current-selection small,
.compact-race-summary small {
  color: #747679;
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.05625rem;
}

.current-selection strong {
  margin-top: 2px;
  font-size: 1rem;
  font-weight: 900;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.current-selection > .v-icon {
  color: #85878a;
}

.compact-race-summary {
  display: flex;
  min-height: 66px;
  margin-bottom: 28px;
  padding: 12px 15px;
  align-items: center;
  justify-content: space-between;
  gap: 13px;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 15px;
  background: rgba(255, 255, 255, 0.028);
}

.compact-race-summary > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.compact-race-summary > span {
  width: 1px;
  height: 29px;
  background: rgba(255, 255, 255, 0.08);
}

.compact-race-summary strong {
  margin-top: 3px;
  color: var(--muted-strong);
  font-size: 0.875rem;
  font-weight: 850;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.form-slide-enter-active,
.form-slide-leave-active {
  transition: opacity 180ms ease, transform 230ms cubic-bezier(0.22, 1, 0.36, 1);
}

.form-slide-enter {
  opacity: 0;
  transform: translateX(18px);
}

.form-slide-leave-to {
  opacity: 0;
  transform: translateX(-12px);
}

.participant-panel {
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.032);
}

.partner-panel {
  margin-top: 12px;
  border-color: rgba(244, 211, 94, 0.2);
  background: rgba(244, 211, 94, 0.025);
}

.participant-label {
  display: flex;
  align-items: center;
  gap: 11px;
}

.avatar-token {
  display: grid;
  width: 40px;
  height: 40px;
  flex: 0 0 auto;
  place-items: center;
  color: var(--accent-ink);
  border-radius: 13px;
  background: var(--accent);
  font-size: 0.75rem;
  font-weight: 950;
}

.partner-token {
  color: var(--accent);
  border: 1px solid rgba(244, 211, 94, 0.3);
  background: rgba(244, 211, 94, 0.06);
}

.participant-label > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.participant-label small {
  color: #747679;
  font-size: 0.75rem;
  font-weight: 950;
  letter-spacing: 0.06875rem;
}

.participant-label strong {
  margin-top: 2px;
  color: var(--ink);
  font-size: 1rem;
  font-weight: 900;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.verified-icon {
  margin-left: auto;
  color: #65c98b !important;
}

.identity-grid {
  display: grid;
  margin-top: 18px;
  grid-template-columns: minmax(0, 1.6fr) minmax(150px, 0.7fr);
  align-items: center;
  gap: 12px;
}

.identity-grid > div:first-child {
  display: flex;
  min-width: 0;
  padding: 0 4px;
  flex-direction: column;
}

.identity-grid small {
  color: #747679;
  font-size: 0.75rem;
  font-weight: 800;
}

.identity-grid span {
  margin-top: 2px;
  color: var(--muted-strong);
  font-size: 0.875rem;
  overflow-wrap: anywhere;
}

.field-grid {
  margin-top: 15px;
}

.time-input-wrap {
  display: grid;
  max-width: 380px;
  padding: 12px 14px;
  grid-template-columns: 30px minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(255, 255, 255, 0.085);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.032);
}

.time-input-wrap > .v-icon {
  color: var(--accent);
}

.time-input-wrap > span {
  color: #77797c;
  font-size: 0.875rem;
  font-weight: 800;
}

.field-hint {
  margin: 8px 0 0 52px;
  color: #707275;
  font-size: 0.875rem;
  line-height: 1.5;
}

.terms-panel {
  margin-top: 34px;
  padding: 13px 17px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.025);
}

.terms-panel span {
  color: #9d9fa2;
  font-size: 1rem;
  line-height: 1.55;
}

.form-alert {
  margin-top: 18px;
  border-radius: 12px !important;
  font-size: 0.875rem;
}

.action-dock {
  display: flex;
  margin-top: 26px;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.form-back-button {
  flex: 0 0 auto;
}

.action-assurance {
  display: flex;
  max-width: 255px;
  align-items: center;
  gap: 8px;
  color: #77797c;
  font-size: 0.875rem;
  line-height: 1.4;
}

.action-assurance .v-icon {
  color: #6fc990;
}

.primary-button {
  min-height: 52px !important;
  padding: 0 25px !important;
  color: var(--accent-ink) !important;
  border-radius: 14px !important;
  background: var(--accent) !important;
  font-size: 0.875rem !important;
  font-weight: 950 !important;
  letter-spacing: 0.009375rem !important;
  text-transform: none !important;
  box-shadow: 0 14px 28px rgba(244, 211, 94, 0.14) !important;
  transition: transform 180ms ease, box-shadow 180ms ease, background 180ms ease !important;
}

.primary-button:hover {
  background: var(--accent-bright) !important;
  box-shadow: 0 18px 34px rgba(244, 211, 94, 0.2) !important;
  transform: translateY(-1px);
}

.primary-button .v-icon {
  color: var(--accent-ink) !important;
}

.state-card {
  width: min(700px, 100%);
  margin: 24px auto 0;
  padding: 55px 48px;
  text-align: center;
  border-radius: 28px;
}

.state-card > p:not(.eyebrow) {
  max-width: 540px;
  margin-right: auto;
  margin-left: auto;
}

.state-icon {
  display: grid;
  width: 70px;
  height: 70px;
  margin: 0 auto 23px;
  place-items: center;
  border-radius: 22px;
}

.pending-icon {
  color: var(--accent);
  border: 1px solid rgba(244, 211, 94, 0.25);
  background: rgba(244, 211, 94, 0.07);
}

.pending-actions {
  display: flex;
  margin-top: 24px;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
}

.closed-icon,
.error-icon {
  color: #ff9d63;
  border: 1px solid rgba(255, 136, 75, 0.2);
  background: rgba(255, 136, 75, 0.065);
}

.state-icon .v-icon {
  color: inherit;
}

.contact-pills {
  display: flex;
  margin-top: 25px;
  flex-wrap: wrap;
  justify-content: center;
  gap: 9px;
}

.contact-pills a {
  display: inline-flex;
  min-height: 42px;
  padding: 0 14px;
  align-items: center;
  gap: 7px;
  color: var(--muted-strong);
  text-decoration: none;
  border: 1px solid var(--stroke);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.035);
  font-size: 0.875rem;
  transition: border-color 180ms ease, background 180ms ease;
}

.contact-pills a:hover {
  border-color: rgba(244, 211, 94, 0.4);
  background: rgba(244, 211, 94, 0.055);
}

.contact-pills .v-icon {
  color: var(--accent);
}

.review-content {
  min-height: 620px;
}

.review-heading {
  margin-bottom: 28px;
}

.race-summary {
  display: flex;
  padding: 19px;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  border: 1px solid rgba(244, 211, 94, 0.25);
  border-radius: 18px;
  background: linear-gradient(135deg, rgba(244, 211, 94, 0.08), rgba(244, 211, 94, 0.02));
}

.race-summary-main {
  display: flex;
  align-items: center;
  gap: 12px;
}

.race-summary-main > div:last-child,
.race-timing > div,
.price-stage > div,
.completion-ticket > div {
  display: flex;
  flex-direction: column;
}

.race-summary-main strong {
  margin-top: 3px;
  color: var(--ink);
  font-size: 1rem;
  font-weight: 900;
}

.race-timing {
  display: flex;
  align-items: center;
  gap: 14px;
}

.race-timing > span {
  width: 1px;
  height: 28px;
  background: rgba(255, 255, 255, 0.1);
}

.race-timing strong {
  margin-top: 2px;
  color: var(--muted-strong);
  font-size: 0.875rem;
}

.review-label {
  margin: 30px 0 10px;
}

.review-participants {
  display: grid;
  gap: 9px;
}

.review-participant {
  display: grid;
  min-height: 80px;
  padding: 14px;
  grid-template-columns: 42px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 15px;
  background: rgba(255, 255, 255, 0.03);
}

.review-person-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.review-person-copy strong {
  margin-top: 1px;
  color: var(--ink);
  font-size: 1rem;
}

.review-person-copy > span {
  margin-top: 1px;
  color: #7e8083;
  font-size: 0.875rem;
  overflow-wrap: anywhere;
}

.review-person-meta {
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  gap: 6px;
}

.review-person-meta > small {
  color: #9b9da0;
}

.membership-pill {
  padding: 4px 7px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 950;
  letter-spacing: 0.04375rem;
}

.membership-pill.member {
  color: #73d697;
  background: rgba(69, 187, 111, 0.1);
}

.membership-pill.nonmember {
  color: #ffb36b;
  background: rgba(255, 154, 60, 0.1);
}

.price-stage {
  display: grid;
  margin-top: 22px;
  padding: 23px;
  grid-template-columns: auto minmax(220px, 360px);
  align-items: center;
  justify-content: space-between;
  gap: 25px;
  border-radius: 18px;
  background: var(--accent);
}

.price-stage small {
  color: rgba(23, 22, 15, 0.55);
}

.price-stage strong {
  display: block;
  color: var(--accent-ink);
  font-size: 2.125rem;
  font-weight: 950;
  letter-spacing: -0.0875rem;
}

.price-stage .regular-price,
.price-stage .trainer-saving {
  display: block;
}

.price-stage .regular-price {
  margin-top: 4px;
  color: rgba(23, 22, 15, 0.58);
  font-size: 0.875rem;
  font-weight: 800;
  text-decoration: line-through;
}

.price-stage .trainer-saving {
  width: fit-content;
  margin-top: 5px;
  padding: 5px 8px;
  color: #102118;
  border-radius: 999px;
  background: rgba(16, 33, 24, 0.11);
  font-size: 0.75rem;
  font-weight: 900;
}

.price-stage p {
  margin: 0;
  color: rgba(23, 22, 15, 0.7);
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.55;
}

.review-actions {
  display: flex;
  margin-top: 25px;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.secondary-button {
  color: #999b9e !important;
  font-size: 0.875rem !important;
  font-weight: 800 !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
}

.payment-content {
  max-width: 680px;
  min-height: 650px;
  margin: 0 auto;
}

.payment-heading {
  text-align: center;
}

.payment-lock {
  display: grid;
  width: 62px;
  height: 62px;
  margin: 0 auto 20px;
  place-items: center;
  color: var(--accent);
  border: 1px solid rgba(244, 211, 94, 0.25);
  border-radius: 20px;
  background: rgba(244, 211, 94, 0.065);
}

.payment-lock .v-icon {
  color: var(--accent);
}

.payment-price {
  margin: 13px 0 0;
  color: var(--accent);
  font-size: 2.1875rem;
  font-weight: 950;
  letter-spacing: -0.09375rem;
}

.payment-loading {
  display: flex;
  min-height: 270px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: var(--muted);
}

.adyen-container {
  min-height: 150px;
  margin-top: 28px;
  padding: 18px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 18px;
  background: #f6f6f4;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.18);
}

/* Adyen Dialog overrides */
::v-deep .adyen-checkout__payment-methods-list {
  padding: 0 !important;
}

::v-deep .adyen-checkout__button__text {
  color: white;
}

::v-deep .adyen-checkout__card__brands__brand-wrapper {
  line-height: 1;
}

::v-deep .adyen-checkout__input-wrapper iframe {
  max-height: 40px !important;
  padding: 0 !important;
  border: none !important;
}

::v-deep .adyen-checkout__input-wrapper {
  height: auto !important;
}

.payment-trust {
  display: flex;
  margin-top: 20px;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 18px;
  color: #77797c;
  font-size: 0.875rem;
}

.payment-cancel-row {
  display: flex;
  margin-top: 14px;
  justify-content: center;
}

.payment-trust span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.payment-trust .v-icon {
  color: #66c98a;
}

.completion-state {
  display: flex;
  min-height: 660px;
  padding: 58px 42px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
}

.completion-mark {
  display: grid;
  width: 98px;
  height: 98px;
  margin-bottom: 25px;
  place-items: center;
  border-radius: 32px;
  transform: rotate(-4deg);
}

.success-mark {
  color: #102118;
  background: #72d596;
  box-shadow: 0 22px 50px rgba(72, 202, 122, 0.16);
}

.review-mark {
  color: var(--accent-ink);
  background: var(--accent);
  box-shadow: 0 22px 50px rgba(244, 211, 94, 0.16);
}

.completion-mark .v-icon {
  color: inherit;
}

.completion-state > p:not(.eyebrow) {
  max-width: 560px;
}

.completion-ticket {
  display: grid;
  width: min(500px, 100%);
  margin: 27px 0;
  padding: 19px;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  text-align: left;
  border: 1px dashed rgba(244, 211, 94, 0.35);
  border-radius: 16px;
  background: rgba(244, 211, 94, 0.045);
}

.completion-ticket > div + div {
  padding-left: 16px;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
}

.completion-ticket strong {
  margin-top: 5px;
  color: var(--ink);
  font-size: 1rem;
}

::v-deep .hyrox-success-dialog {
  margin: 20px;
  border-radius: 28px;
}

.success-dialog-card {
  --ink: #f8f7f2;
  --muted-strong: #c8c8c5;
  --accent: #f4d35e;
  --accent-ink: #17160f;
  position: relative;
  overflow: hidden;
  padding: 42px;
  color: var(--ink);
  border: 1px solid rgba(244, 211, 94, 0.24) !important;
  border-radius: 28px !important;
  background: radial-gradient(circle at 100% 0, rgba(244, 211, 94, 0.13), transparent 19rem), #151617 !important;
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.55) !important;
  font-family: Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-size: 1rem;
  text-align: center;
}

.success-dialog-card::before {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 4px;
  content: "";
  background: var(--accent);
}

.success-dialog-close {
  position: absolute;
  top: 15px;
  right: 15px;
  display: grid;
  width: 44px;
  height: 44px;
  place-items: center;
  color: var(--muted-strong);
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.04);
}

.success-dialog-close .v-icon {
  color: inherit;
}

.success-dialog-icon {
  display: grid;
  width: 78px;
  height: 78px;
  margin: 0 auto 22px;
  place-items: center;
  color: #102118;
  border-radius: 25px;
  background: #72d596;
  box-shadow: 0 20px 42px rgba(72, 202, 122, 0.18);
}

.success-dialog-icon .v-icon {
  color: inherit;
}

.success-dialog-card h2 {
  margin: 5px 0 0;
  color: var(--ink);
  font-size: 2rem;
  font-weight: 950;
  line-height: 1.08;
  letter-spacing: -0.06875rem;
}

.success-dialog-card > p:not(.eyebrow) {
  max-width: 430px;
  margin: 16px auto 0;
  color: var(--muted-strong);
  font-size: 1rem;
  line-height: 1.65;
}

.success-dialog-button {
  min-width: 190px !important;
  margin-top: 27px;
}

.centered-pills {
  justify-content: center;
}

::v-deep .v-window,
::v-deep .v-window__container {
  overflow: visible;
}

::v-deep .v-text-field--outlined fieldset {
  border-color: rgba(255, 255, 255, 0.11);
  border-radius: 12px;
  transition: border-color 180ms ease;
}

::v-deep .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: var(--accent) !important;
}

::v-deep .v-input {
  color: var(--ink);
  font-size: 1rem;
}

::v-deep .v-label {
  color: #85878a !important;
  font-size: 0.875rem;
}

::v-deep .v-input input {
  color: var(--ink) !important;
}

::v-deep .time-input-wrap .v-input__slot {
  min-height: 42px !important;
  margin: 0 !important;
}

::v-deep .time-input-wrap fieldset {
  border: 0 !important;
}

::v-deep .time-input-wrap .v-text-field__details {
  display: none;
}

::v-deep .terms-panel .v-input--selection-controls {
  margin: 0;
}

::v-deep .terms-panel .v-messages__message {
  padding-top: 5px;
}

@media (max-width: 1024px) {
  .registration-topbar {
    grid-template-columns: 1fr auto 1fr;
    padding: 0 20px;
  }

  .brand-lockup {
    display: none;
  }

  .event-stage {
    padding: 30px;
    grid-template-columns: auto minmax(0, 1fr);
  }

  .event-intro h1 {
    font-size: 2.5rem;
  }

  .event-badge {
    display: none;
  }

  .flow-layout {
    grid-template-columns: 230px minmax(0, 1fr);
    gap: 16px;
  }

  .rail-card {
    padding: 18px;
  }

  .flow-content {
    padding: 34px;
  }

  .race-summary {
    align-items: flex-start;
    flex-direction: column;
  }

  .race-timing {
    width: 100%;
    justify-content: space-between;
  }
}

@media (max-width: 820px) {
  .simulation-container {
    width: min(100% - 28px, 720px);
    padding-top: 18px;
  }

  .event-stage {
    min-height: 210px;
    padding: 25px;
  }

  .event-date-block {
    width: 88px;
    height: 120px;
  }

  .event-date-block strong {
    font-size: 2.5rem;
  }

  .event-intro h1 {
    letter-spacing: -0.09375rem;
  }

  .event-intro > p {
    display: none;
  }

  .event-facts {
    margin-top: 16px;
  }

  .flow-layout {
    display: block;
  }

  .event-rail {
    position: static;
    margin-bottom: 14px;
  }

  .rail-card {
    display: grid;
    padding: 15px 17px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
  }

  .rail-heading,
  .rail-price,
  .mini-schedule,
  .rail-divider {
    display: none;
  }

  .rail-detail {
    margin: 0;
  }
}

@media (max-width: 600px) {
  .background-grid {
    background-size: 34px 34px;
  }

  .registration-topbar {
    grid-template-columns: 40px minmax(0, 1fr) 24px;
    min-height: 62px;
    padding: 0 14px;
    column-gap: 9px;
  }

  .nav-back {
    width: 40px;
    height: 40px;
    color: var(--accent);
    border-color: rgba(244, 211, 94, 0.3);
    border-radius: 13px;
    background: rgba(244, 211, 94, 0.065);
    box-shadow: none;
  }

  .nav-back .v-icon {
    color: var(--accent) !important;
  }

  .nav-back:hover {
    border-color: rgba(244, 211, 94, 0.5);
    background: rgba(244, 211, 94, 0.1);
  }

  .desktop-top-progress {
    display: none;
  }

  .mobile-top-progress {
    position: relative;
    display: flex;
    grid-column: 2;
    grid-row: 1;
    width: min(100%, 238px);
    justify-self: center;
    justify-content: space-between;
  }

  .secure-label span {
    display: none;
  }

  .simulation-container {
    width: calc(100% - 20px);
    padding-top: 10px;
    padding-bottom: 100px;
  }

  .event-stage {
    min-height: 178px;
    padding: 21px 18px;
    grid-template-columns: 68px minmax(0, 1fr);
    gap: 15px;
    border-radius: 22px;
  }

  .event-date-block {
    width: 68px;
    height: 96px;
    border-radius: 17px;
  }

  .event-date-block strong {
    font-size: 2.0625rem;
    letter-spacing: -0.125rem;
  }

  .event-date-block span {
    margin-top: 6px;
    font-size: 0.75rem;
  }

  .event-date-block small {
    font-size: 0.75rem;
  }

  .event-kicker {
    margin-bottom: 7px;
    font-size: 0.75rem;
    letter-spacing: 0.0625rem;
  }

  .event-intro h1 {
    font-size: 1.9375rem;
    line-height: 0.92;
    letter-spacing: -0.0625rem;
  }

  .event-title span:last-child::after {
    right: -12px;
    bottom: 3px;
    width: 5px;
    height: 5px;
    box-shadow: 0 0 0 3px rgba(244, 211, 94, 0.1);
  }

  .event-facts {
    margin-top: 11px;
  }

  .event-facts span {
    min-height: 34px;
    padding: 5px 10px;
    font-size: 0.875rem;
  }

  .event-facts span:nth-child(n + 2) {
    display: none;
  }

  .event-rail {
    margin-bottom: 10px;
  }

  .rail-card {
    padding: 12px;
    grid-template-columns: 1fr 1fr;
    border-radius: 17px;
  }

  .rail-detail:nth-of-type(3) {
    display: none;
  }

  .rail-detail {
    grid-template-columns: 19px 1fr;
    gap: 7px;
  }

  .rail-detail strong {
    font-size: 0.875rem;
  }

  .rail-detail span {
    display: none;
  }

  .flow-card {
    overflow: visible;
    border-radius: 22px !important;
  }

  .flow-content {
    padding: 26px 17px;
  }

  .flow-heading {
    margin-bottom: 30px;
  }

  .flow-heading h2,
  .state-card h1,
  .state-card h2,
  .completion-state h2,
  .payment-heading h2 {
    font-size: 1.6875rem;
    letter-spacing: -0.05625rem;
  }

  .flow-heading > p:last-child {
    font-size: 1rem;
  }

  .trainer-benefit {
    margin: -8px 0 28px;
    padding: 15px;
    grid-template-columns: 42px minmax(0, 1fr);
    gap: 11px;
  }

  .trainer-benefit-icon {
    width: 42px;
    height: 42px;
    border-radius: 13px;
  }

  .form-stepper {
    display: none;
  }

  .event-guidance {
    margin-bottom: 28px;
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .guidance-item {
    padding: 14px;
    grid-template-columns: 38px minmax(0, 1fr);
    gap: 10px;
  }

  .guidance-icon {
    width: 38px;
    height: 38px;
    border-radius: 12px;
  }

  .form-section + .form-section {
    margin-top: 32px;
    padding-top: 32px;
  }

  .section-heading {
    gap: 10px;
  }

  .section-heading p {
    max-width: 250px;
  }

  .category-grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .category-option {
    min-height: 88px;
    grid-template-columns: 42px minmax(0, 1fr) auto;
    grid-template-rows: 1fr 1fr;
  }

  .category-option .category-icon {
    grid-row: 1 / 3;
  }

  .category-option .category-copy {
    grid-row: 1 / 3;
  }

  .category-option .availability {
    min-width: max-content;
    margin: 0;
    padding: 4px 7px;
    grid-row: 2;
    grid-column: 3;
    align-self: start;
    justify-self: end;
    white-space: nowrap;
  }

  .category-option .selected-check {
    position: static;
    grid-row: 1;
    grid-column: 3;
    align-self: end;
    justify-self: end;
  }

  .participant-panel {
    padding: 16px 13px;
  }

  .identity-grid {
    grid-template-columns: 1fr;
  }

  .identity-grid > div:first-child {
    padding: 0 2px 9px;
  }

  .field-grid > .col {
    padding-top: 5px;
    padding-bottom: 5px;
  }

  .time-input-wrap {
    max-width: none;
  }

  .compact-race-summary {
    padding: 11px;
    gap: 8px;
  }

  .compact-race-summary strong {
    font-size: 0.875rem;
  }

  .field-hint {
    margin-left: 0;
  }

  .terms-panel {
    margin-top: 27px;
    padding: 10px 12px;
  }

  .action-dock {
    position: sticky;
    z-index: 12;
    bottom: 0;
    margin: 25px -17px -26px;
    padding: 13px 17px calc(13px + env(safe-area-inset-bottom));
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(18, 19, 20, 0.94);
    backdrop-filter: blur(18px);
    -webkit-backdrop-filter: blur(18px);
  }

  .action-assurance {
    display: none;
  }

  .action-dock .primary-button {
    min-width: 0;
    padding-right: 14px !important;
    padding-left: 14px !important;
    flex: 1 1 auto;
  }

  .form-back-button {
    min-width: 82px !important;
    padding: 0 8px !important;
  }

  .state-card {
    margin-top: 10px;
    padding: 42px 20px;
    border-radius: 22px;
  }

  .contact-pills {
    align-items: stretch;
    flex-direction: column;
  }

  .contact-pills a {
    justify-content: center;
  }

  .race-summary {
    padding: 15px;
  }

  .race-timing {
    gap: 8px;
  }

  .race-timing strong {
    font-size: 0.875rem;
  }

  .review-participant {
    grid-template-columns: 38px minmax(0, 1fr);
  }

  .review-participant .avatar-token {
    width: 38px;
    height: 38px;
  }

  .review-person-meta {
    grid-column: 2;
    align-items: center;
    flex-direction: row;
  }

  .price-stage {
    padding: 19px;
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .price-stage strong {
    font-size: 1.9375rem;
  }

  .review-actions {
    align-items: stretch;
    flex-direction: column-reverse;
  }

  .review-actions .primary-button {
    width: 100%;
  }

  .payment-content {
    min-height: 570px;
  }

  .adyen-container {
    margin: 25px -7px 0;
    padding: 12px;
    border-radius: 14px;
  }

  .completion-state {
    min-height: 570px;
    padding: 45px 20px;
  }

  .completion-ticket {
    grid-template-columns: 1fr;
  }

  .completion-ticket > div + div {
    padding-top: 13px;
    padding-left: 0;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    border-left: 0;
  }

  ::v-deep .hyrox-success-dialog {
    width: calc(100% - 24px);
    margin: 12px;
  }

  .success-dialog-card {
    padding: 38px 20px 25px;
    border-radius: 22px !important;
  }

  .success-dialog-card h2 {
    font-size: 1.75rem;
  }

  .success-dialog-button {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
  }
}
</style>
